import React, { useState, useEffect } from 'react';
import {
  Users, TrendingUp, Award, Calendar, Search, Filter, Eye, AlertCircle,
  Clock, CheckCircle, UserCheck, Target, Activity, FileText, MessageCircle,
  Download, Upload, BarChart3, PieChart, Mail, Send, ChevronRight, X, Sparkles
} from 'lucide-react';
import axios from 'axios';
import MentorAssignments from './MentorAssignments';
import TourSystem, { EnhancedTourButton } from './TourSystem';

const MentorDashboardEnhanced = () => {
  const [analytics, setAnalytics] = useState(null);
  const [mentees, setMentees] = useState([]);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMentorTour, setShowMentorTour] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTrack, setFilterTrack] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [selectedMentee, setSelectedMentee] = useState(null);
  const [menteeDetails, setMenteeDetails] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // New state for quick actions and reports
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [showQuickMessageModal, setShowQuickMessageModal] = useState(false);
  const [bulkEmailData, setBulkEmailData] = useState({ subject: '', message: '', cohort: '', track: '', tier: '' });
  const [quickMessageData, setQuickMessageData] = useState({ mentee_id: '', subject: '', message: '' });
  const [engagementTrends, setEngagementTrends] = useState(null);
  const [courseCompletion, setCourseCompletion] = useState(null);
  const [cohortComparison, setCohortComparison] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchDashboardData();
  }, [filterTrack, filterTier]);

  const fetchDashboardData = async () => {
    try {
      // Fetch analytics
      const analyticsRes = await axios.get(`${backendUrl}/api/mentor/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAnalytics(analyticsRes.data);

      // Fetch mentees with filters
      const params = new URLSearchParams();
      if (filterTrack) params.append('track', filterTrack);
      if (filterTier) params.append('tier', filterTier);
      
      const menteesRes = await axios.get(
        `${backendUrl}/api/mentor/mentees?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setMentees(menteesRes.data);

      // Fetch suggested actions
      const actionsRes = await axios.get(`${backendUrl}/api/mentor/track/suggested-actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuggestedActions(actionsRes.data);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 403) {
        alert('Access denied. Only admin users can access the mentor dashboard.');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewMenteeDetails = async (menteeId) => {
    try {
      const response = await axios.get(`${backendUrl}/api/mentor/track/mentee/${menteeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMenteeDetails(response.data);
      setSelectedMentee(menteeId);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching mentee details:', error);
      alert('Failed to load mentee details');
    }
  };

  // Quick Action Functions
  const sendBulkEmail = async () => {
    if (!bulkEmailData.subject || !bulkEmailData.message) {
      alert('Please fill in subject and message');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.post(`${backendUrl}/api/mentor/quick-actions/bulk-email`, bulkEmailData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert(`Success! ${response.data.message}`);
      setShowBulkEmailModal(false);
      setBulkEmailData({ subject: '', message: '', cohort: '', track: '', tier: '' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send bulk email');
    } finally {
      setActionLoading(false);
    }
  };

  const sendQuickMessage = async () => {
    if (!quickMessageData.mentee_id || !quickMessageData.subject || !quickMessageData.message) {
      alert('Please fill in all fields');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${backendUrl}/api/mentor/quick-actions/message-mentee`, quickMessageData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Message sent successfully!');
      setShowQuickMessageModal(false);
      setQuickMessageData({ mentee_id: '', subject: '', message: '' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setActionLoading(false);
    }
  };

  const exportMentees = async () => {
    try {
      const params = new URLSearchParams();
      if (filterTrack) params.append('track', filterTrack);
      if (filterTier) params.append('tier', filterTier);
      
      const response = await axios.get(`${backendUrl}/api/mentor/quick-actions/export-mentees?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Convert to CSV
      const data = response.data.data;
      if (data.length === 0) {
        alert('No data to export');
        return;
      }
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mentees_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export data');
    }
  };

  // Fetch Reports Data
  const fetchReportsData = async () => {
    try {
      const [trendsRes, completionRes, cohortRes] = await Promise.all([
        axios.get(`${backendUrl}/api/mentor/reports/engagement-trends?period=30d`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${backendUrl}/api/mentor/reports/course-completion`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${backendUrl}/api/mentor/reports/cohort-comparison`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setEngagementTrends(trendsRes.data);
      setCourseCompletion(completionRes.data);
      setCohortComparison(cohortRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReportsData();
    }
  }, [activeTab]);

  const filteredMentees = mentees.filter(mentee => {
    const search = searchTerm.toLowerCase();
    return (
      mentee.name.toLowerCase().includes(search) ||
      mentee.email.toLowerCase().includes(search)
    );
  });

  // Get expiring soon mentees
  const expiringSoon = mentees.filter(m => m.days_remaining !== null && m.days_remaining > 0 && m.days_remaining <= 30);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading mentor dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      {/* Mentor Tour */}
      <TourSystem 
        tourType="mentor" 
        showTour={showMentorTour} 
        setShowTour={setShowMentorTour} 
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Mentor Dashboard</h1>
            <p className="text-gray-600">Track and manage your 12-month mentorship programs</p>
          </div>
          <EnhancedTourButton 
            onClick={() => setShowMentorTour(true)} 
            variant="default" 
          />
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {['overview', 'mentees', 'assignments', 'cohorts', 'reports', 'actions'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div>
            {/* Analytics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-purple-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total Mentees</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.total_mentees}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  {analytics.active_mentees} active in last 30 days
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-green-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Active Programs</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.active_mentorships}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Within 12-month period
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-orange-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Expiring Soon</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.expiring_soon}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Ending in next 30 days
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6 border-l-4 border-blue-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Completion Rate</p>
                    <p className="text-3xl font-bold text-gray-900">{analytics.avg_completion_rate}%</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  {analytics.total_courses_completed} courses completed
                </p>
              </div>
            </div>

            {/* Expiring Soon Alert */}
            {expiringSoon.length > 0 && (
              <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg mb-8">
                <div className="flex items-start">
                  <AlertCircle className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2">
                      ⚠️ {expiringSoon.length} Mentorship(s) Expiring Soon
                    </h3>
                    <p className="text-sm text-orange-800 mb-4">
                      The following mentees are approaching their 12-month completion. Reach out for renewal conversations:
                    </p>
                    <div className="space-y-2">
                      {expiringSoon.slice(0, 5).map((mentee) => (
                        <div key={mentee.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{mentee.name}</p>
                            <p className="text-sm text-gray-500">{mentee.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-orange-600">{mentee.days_remaining} days left</p>
                            <p className="text-xs text-gray-500">{mentee.months_completed?.toFixed(1)}/12 months</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button
                  onClick={() => setShowBulkEmailModal(true)}
                  className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Bulk Email</p>
                    <p className="text-xs text-gray-500">Email a cohort</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setShowQuickMessageModal(true)}
                  className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Message Mentee</p>
                    <p className="text-xs text-gray-500">Quick direct message</p>
                  </div>
                </button>
                
                <button
                  onClick={exportMentees}
                  className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Export Data</p>
                    <p className="text-xs text-gray-500">Download CSV</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setActiveTab('reports')}
                  className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">View Reports</p>
                    <p className="text-xs text-gray-500">Analytics & trends</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Track Distribution */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Enrollment by Track</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-purple-50 rounded-xl">
                  <p className="text-3xl font-bold text-purple-600">{analytics.by_track.apostolic_prophetic}</p>
                  <p className="text-sm text-gray-600 mt-1">Apostolic-Prophetic</p>
                </div>
                <div className="text-center p-4 bg-orange-50 rounded-xl">
                  <p className="text-3xl font-bold text-orange-600">{analytics.by_track.minstrel_worship}</p>
                  <p className="text-sm text-gray-600 mt-1">Minstrel & Worship</p>
                </div>
                <div className="text-center p-4 bg-gray-100 rounded-xl">
                  <p className="text-3xl font-bold text-gray-600">{analytics.by_track.not_enrolled}</p>
                  <p className="text-sm text-gray-600 mt-1">Not Yet Enrolled</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mentees Tab */}
        {activeTab === 'mentees' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
              <h2 className="text-2xl font-semibold text-gray-900">All Mentees ({filteredMentees.length})</h2>
              
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search mentees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  />
                </div>

                <select
                  value={filterTrack}
                  onChange={(e) => setFilterTrack(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">All Tracks</option>
                  <option value="apostolic-prophetic">Apostolic-Prophetic</option>
                  <option value="minstrel-worship">Minstrel & Worship</option>
                </select>

                <select
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">All Tiers</option>
                  <option value="guest">Guest</option>
                  <option value="mentorship">Mentorship</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Track</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Progress</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">12-Month Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Points</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMentees.map((mentee) => (
                    <tr key={mentee.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{mentee.name}</p>
                          <p className="text-sm text-gray-500">{mentee.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {mentee.enrolled_track ? (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                            {mentee.enrolled_track === 'apostolic-prophetic' ? 'Apostolic' : 'Minstrel'}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">Not enrolled</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">{mentee.completed_courses}/{mentee.enrolled_courses}</span>
                          {mentee.enrolled_courses > 0 && (
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full" 
                                style={{width: `${(mentee.completed_courses / mentee.enrolled_courses) * 100}%`}}
                              ></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {mentee.days_remaining !== null ? (
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {mentee.days_remaining > 0 ? `${mentee.days_remaining} days left` : 'Expired'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {mentee.months_completed?.toFixed(1)}/12 months
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not enrolled</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-gray-900">{mentee.points}</span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => viewMenteeDetails(mentee.id)}
                          className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center space-x-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredMentees.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No mentees found matching your criteria</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cohorts Tab */}
        {activeTab === 'assignments' && (
          <MentorAssignments />
        )}

        {activeTab === 'cohorts' && analytics && (
          <div>
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Mentees by Cohort</h2>
              <p className="text-gray-600 mb-6">Grouped by enrollment quarter (12-month programs)</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(analytics.by_cohort).sort(([a], [b]) => b.localeCompare(a)).map(([cohort, count]) => {
                  const cohortMentees = mentees.filter(m => m.mentorship_cohort === cohort);
                  const active = cohortMentees.filter(m => m.days_remaining > 0).length;
                  const expired = cohortMentees.filter(m => m.days_remaining === 0).length;
                  const avgCompletion = cohortMentees.reduce((sum, m) => sum + (m.months_completed || 0), 0) / cohortMentees.length;
                  
                  return (
                    <div key={cohort} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-gray-900">{cohort}</h3>
                        <span className="text-2xl font-bold text-purple-600">{count}</span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Active:</span>
                          <span className="font-semibold text-green-600">{active}</span>
                        </div>
                        {expired > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Expired:</span>
                            <span className="font-semibold text-gray-500">{expired}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Avg Progress:</span>
                          <span className="font-semibold text-blue-600">{avgCompletion.toFixed(1)}/12 mo</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button 
                          onClick={() => {
                            setFilterTier('');
                            setActiveTab('mentees');
                          }}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          View Mentees →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Engagement Summary */}
            {engagementTrends && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Engagement Overview (Last 30 Days)</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-purple-50 p-4 rounded-xl text-center">
                    <p className="text-3xl font-bold text-purple-600">{engagementTrends.summary.total_messages}</p>
                    <p className="text-sm text-gray-600">Community Messages</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl text-center">
                    <p className="text-3xl font-bold text-blue-600">{engagementTrends.summary.total_appointments}</p>
                    <p className="text-sm text-gray-600">Appointments Booked</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl text-center">
                    <p className="text-3xl font-bold text-green-600">{engagementTrends.summary.avg_daily_messages}</p>
                    <p className="text-sm text-gray-600">Avg Daily Messages</p>
                  </div>
                </div>
              </div>
            )}

            {/* Course Completion Report */}
            {courseCompletion && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">📚 Course Completion Rates</h2>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{courseCompletion.overall.total_enrollments}</p>
                    <p className="text-sm text-gray-600">Total Enrollments</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <p className="text-2xl font-bold text-green-600">{courseCompletion.overall.total_completions}</p>
                    <p className="text-sm text-gray-600">Total Completions</p>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600">{courseCompletion.overall.avg_completion_rate}%</p>
                    <p className="text-sm text-gray-600">Avg Completion Rate</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {courseCompletion.courses.slice(0, 5).map((course) => (
                    <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{course.title}</p>
                        <p className="text-xs text-gray-500">{course.track || 'General'}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-600">{course.enrolled} enrolled</span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded-full"
                            style={{ width: `${course.completion_rate}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-900">{course.completion_rate}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cohort Comparison */}
            {cohortComparison && cohortComparison.cohorts.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">🎯 Cohort Performance Comparison</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cohort</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Members</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Active</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Engagement</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Avg Points</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Avg Courses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohortComparison.cohorts.map((cohort) => (
                        <tr key={cohort.cohort} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{cohort.cohort}</td>
                          <td className="py-3 px-4">{cohort.members}</td>
                          <td className="py-3 px-4">{cohort.active_members}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              cohort.engagement_rate >= 70 ? 'bg-green-100 text-green-700' :
                              cohort.engagement_rate >= 40 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {cohort.engagement_rate}%
                            </span>
                          </td>
                          <td className="py-3 px-4">{cohort.avg_points}</td>
                          <td className="py-3 px-4">{cohort.avg_courses_completed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Suggested Actions Tab */}
        {activeTab === 'actions' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Suggested Actions</h2>
            <p className="text-gray-600 mb-6">AI-powered recommendations based on mentee behavior</p>
            
            {suggestedActions.length > 0 ? (
              <div className="space-y-4">
                {suggestedActions.map((action, index) => (
                  <div 
                    key={index}
                    className={`border-l-4 p-4 rounded-lg ${
                      action.priority === 'high' ? 'border-red-500 bg-red-50' :
                      action.priority === 'medium' ? 'border-orange-500 bg-orange-50' :
                      'border-blue-500 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            action.priority === 'high' ? 'bg-red-200 text-red-800' :
                            action.priority === 'medium' ? 'bg-orange-200 text-orange-800' :
                            'bg-blue-200 text-blue-800'
                          }`}>
                            {action.priority.toUpperCase()}
                          </span>
                          <span className="text-xs text-gray-500">{action.action_type}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{action.title}</h3>
                        <p className="text-sm text-gray-700">{action.description}</p>
                      </div>
                      <button
                        onClick={() => viewMenteeDetails(action.mentee_id)}
                        className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                      >
                        View Mentee
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-gray-600">No urgent actions at this time. Great job!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mentee Detail Modal */}
      {showDetailModal && menteeDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{menteeDetails.name}</h2>
                <p className="text-gray-500">{menteeDetails.email}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 12-Month Progress */}
            {menteeDetails.days_remaining !== null && (
              <div className="bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">12-Month Program Progress</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">{menteeDetails.months_completed?.toFixed(1)}</p>
                    <p className="text-sm text-gray-600">Months Completed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-orange-600">{menteeDetails.days_remaining}</p>
                    <p className="text-sm text-gray-600">Days Remaining</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{Math.round(((menteeDetails.months_completed || 0) / 12) * 100)}%</p>
                    <p className="text-sm text-gray-600">Time Progress</p>
                  </div>
                </div>
                <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-600 to-orange-600 rounded-full transition-all"
                    style={{width: `${Math.min(((menteeDetails.months_completed || 0) / 12) * 100, 100)}%`}}
                  ></div>
                </div>
              </div>
            )}

            {/* Rest of mentee details... */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{menteeDetails.engagement_score}</p>
                <p className="text-xs text-gray-600 mt-1">Engagement Score</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-orange-600">{menteeDetails.total_ebooks_downloaded}</p>
                <p className="text-xs text-gray-600 mt-1">Ebooks Downloaded</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{menteeDetails.pending_reviews}</p>
                <p className="text-xs text-gray-600 mt-1">Pending Reviews</p>
              </div>
            </div>

            <p className="text-center text-gray-500 text-sm mt-4">
              Full detailed tracking coming soon...
            </p>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowBulkEmailModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">📧 Send Bulk Email</h2>
              <button onClick={() => setShowBulkEmailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <select
                  value={bulkEmailData.cohort}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, cohort: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Cohorts</option>
                  {analytics?.by_cohort && Object.keys(analytics.by_cohort).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={bulkEmailData.track}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, track: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Tracks</option>
                  <option value="apostolic-prophetic">Apostolic-Prophetic</option>
                  <option value="minstrel-worship">Minstrel & Worship</option>
                </select>
                <select
                  value={bulkEmailData.tier}
                  onChange={(e) => setBulkEmailData({...bulkEmailData, tier: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">All Tiers</option>
                  <option value="guest">Guest</option>
                  <option value="mentorship">Mentorship</option>
                </select>
              </div>
              
              <input
                type="text"
                placeholder="Email Subject"
                value={bulkEmailData.subject}
                onChange={(e) => setBulkEmailData({...bulkEmailData, subject: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              
              <textarea
                placeholder="Email Message"
                value={bulkEmailData.message}
                onChange={(e) => setBulkEmailData({...bulkEmailData, message: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none"
                rows={5}
              />
              
              <button
                onClick={sendBulkEmail}
                disabled={actionLoading}
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 font-medium"
              >
                {actionLoading ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Message Modal */}
      {showQuickMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowQuickMessageModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">💬 Quick Message</h2>
              <button onClick={() => setShowQuickMessageModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <select
                value={quickMessageData.mentee_id}
                onChange={(e) => setQuickMessageData({...quickMessageData, mentee_id: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Mentee</option>
                {mentees.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
              
              <input
                type="text"
                placeholder="Subject"
                value={quickMessageData.subject}
                onChange={(e) => setQuickMessageData({...quickMessageData, subject: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              
              <textarea
                placeholder="Your message..."
                value={quickMessageData.message}
                onChange={(e) => setQuickMessageData({...quickMessageData, message: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none"
                rows={4}
              />
              
              <button
                onClick={sendQuickMessage}
                disabled={actionLoading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-medium"
              >
                {actionLoading ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorDashboardEnhanced;